import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Card } from '../../../../layout/Card.jsx';
import { Icon } from '../../../../layout/Icon.jsx';
import { toast } from '../../../../shared/useToast.js';
import { useSettings, useUpdateSettings } from '../../../../shared/useSettings.js';
import { PrintSettingsCard } from './PrintSettingsCard.jsx';

const PRICING_TYPES = [
  { key: 'hourly', label: 'ساعي', sub: 'حسب مدة البقاء', icon: '⏱️' },
  { key: 'open', label: 'وقت مفتوح', sub: 'مبلغ مفتوح بدون وقت', icon: '♾️' },
  { key: 'fixed', label: 'مبلغ ثابت', sub: 'دخول بمبلغ مقطوع', icon: '🎫' },
  { key: 'package', label: 'الباقات', sub: 'باقات بمدّة محددة', icon: '🎉' }
];

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange?.(!checked)}
      className={clsx(
        'relative w-9 h-5 rounded-full transition-colors flex-none',
        checked ? 'bg-orange' : 'bg-[#E5E0D5]'
      )}
    >
      <span
        className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow"
        style={{ insetInlineStart: checked ? 18 : 2, transition: 'inset-inline-start .15s' }}
      />
    </button>
  );
}

const ROUND_PRESETS = [0, 250, 500, 1000];

// سياسة كلفة المنتج عند ترحيل فاتورة شراء مع خيار «تحديث كلفة المنتج».
const COST_POLICIES = [
  {
    key: 'last',
    label: 'الكلفة الأخيرة المدخلة',
    sub: 'كلفة المنتج تصبح الكلفة المكتوبة في الفاتورة (2,000 تُسجَّل 2,000). الافتراضي.',
    icon: '🧾'
  },
  {
    key: 'weighted',
    label: 'المتوسط المرجّح',
    sub: '(المخزون القديم × كلفته + الكمية الواردة × كلفتها) ÷ المجموع — تتحرّك الكلفة قليلاً مع مخزون كبير.',
    icon: '⚖️'
  }
];

function normalizeCostPolicy(v) {
  return v === 'weighted' ? 'weighted' : 'last';
}

export function GeneralTab() {
  const { data, isLoading } = useSettings();
  const updateMut = useUpdateSettings();
  // حفظ الاسم بلا رسالة نجاح تلقائية: لا نُعلن النجاح إلا بعد التأكّد من أن
  // الخادم أعاد القيمة المحفوظة فعلاً (كانت تُقبل 200 بلا كتابة في القاعدة).
  const nameMut = useUpdateSettings({ successMessage: null });
  const [hidden, setHidden] = useState([]);
  const [businessName, setBusinessName] = useState('');
  const [companionFee, setCompanionFee] = useState(0);
  const [roundUpTo, setRoundUpTo] = useState(1000);
  const [costPolicy, setCostPolicy] = useState('last');

  useEffect(() => {
    setHidden(Array.isArray(data?.hiddenPricingTypes) ? data.hiddenPricingTypes : []);
    setBusinessName(String(data?.businessName || ''));
    setCompanionFee(Math.max(0, Math.round(Number(data?.companionEntryFee) || 0)));
    setRoundUpTo(Number.isFinite(Number(data?.roundUpTo)) ? Number(data.roundUpTo) : 1000);
    setCostPolicy(normalizeCostPolicy(data?.productCostPolicy));
  }, [data]);

  function toggleVisible(key) {
    setHidden((h) => {
      const arr = Array.isArray(h) ? h : [];
      return arr.includes(key) ? arr.filter((k) => k !== key) : [...arr, key];
    });
  }

  const serverBusinessName = String(data?.businessName || '');
  const nameDirty = businessName.trim() !== serverBusinessName;

  async function saveBusinessName() {
    const next = businessName.trim();
    try {
      const saved = await nameMut.mutateAsync({ businessName: next });
      if (String(saved?.businessName ?? '') !== next) {
        toast.error('تعذّر حفظ اسم الصالة — لم يُخزَّن الاسم في قاعدة البيانات');
        setBusinessName(String(saved?.businessName || ''));
        return;
      }
      toast.success('تم حفظ التغييرات بنجاح');
    } catch (_) {
      // useApiMutation أظهر رسالة الخطأ — ولا نُظهر أي إشارة نجاح.
    }
  }

  const serverHidden = Array.isArray(data?.hiddenPricingTypes) ? data.hiddenPricingTypes : [];
  const dirty = JSON.stringify([...hidden].sort()) !== JSON.stringify([...serverHidden].sort());

  const serverFee = Math.max(0, Math.round(Number(data?.companionEntryFee) || 0));
  const feeDirty = Number(companionFee) !== serverFee;

  const serverRound = Number.isFinite(Number(data?.roundUpTo)) ? Number(data.roundUpTo) : 1000;
  const roundClean = Math.max(0, Math.round(Number(roundUpTo) || 0));
  const roundDirty = roundClean !== serverRound;

  const serverCostPolicy = normalizeCostPolicy(data?.productCostPolicy);
  const costPolicyDirty = costPolicy !== serverCostPolicy;

  return (
    <div className="flex flex-col gap-3">
      <Card header="اسم الصالة على الإيصالات">
        <div className="text-ink-3 text-[12px] mb-3">
          يظهر هذا الاسم في رأس إيصالات الطباعة والتقارير. اتركه فارغاً لاستخدام اسم الشركة المسجّل.
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div style={{ width: 320 }}>
            <input
              className="input"
              maxLength={120}
              placeholder="مثال: مدينة النجوم للألعاب"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={saveBusinessName}
            disabled={isLoading || nameMut.isPending || !nameDirty}
          >
            <Icon name="check" size={14} />
            {nameMut.isPending ? 'جاري الحفظ...' : 'حفظ الاسم'}
          </button>
        </div>
      </Card>

      <PrintSettingsCard />

      <Card header="أنواع التسعير الظاهرة">
        <div className="text-ink-3 text-[12px] mb-3">
          أطفئ أي نوع لا تستخدمه ليُخفى من شاشة الاستقبال وإعداد التسعير. لا يؤثر ذلك على الجلسات السابقة.
        </div>
        <div className="grid grid-cols-2 gap-2">
          {PRICING_TYPES.map((t) => {
            const visible = !hidden.includes(t.key);
            return (
              <div
                key={t.key}
                className={clsx(
                  'flex items-center gap-2.5 p-2.5 rounded-[10px] border border-line transition-colors',
                  visible ? 'bg-[#FFFCF5]' : 'bg-white opacity-80'
                )}
              >
                <Toggle checked={visible} onChange={() => toggleVisible(t.key)} />
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-semibold">{t.icon} {t.label}</div>
                  <div className="text-[11px] text-ink-3 truncate">{t.sub}</div>
                </div>
                <span className={clsx('text-[11px] font-semibold', visible ? 'text-green' : 'text-ink-3')}>
                  {visible ? 'ظاهر' : 'مخفي'}
                </span>
              </div>
            );
          })}
        </div>
        <div className="flex justify-end mt-4">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => updateMut.mutate({ hiddenPricingTypes: hidden })}
            disabled={isLoading || updateMut.isPending || !dirty}
          >
            <Icon name="check" size={14} />
            {updateMut.isPending ? 'جاري الحفظ...' : 'حفظ التغييرات'}
          </button>
        </div>
      </Card>

      <Card header="رسم دخول المرافقين">
        <div className="text-ink-3 text-[12px] mb-3">
          المرافق هو شخص بالغ يدخل مع الأطفال بدون كارت خاص به. يُضاف عدد المرافقين
          على الكارت في شاشة الاستقبال، ويُحتسب هذا الرسم لكل مرافق ويُحصَّل عند الدخول.
          اتركه صفراً إذا كان دخول المرافقين مجانياً (يُسجَّل العدد فقط).
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div style={{ width: 200 }}>
            <label className="block text-[11.5px] font-semibold text-ink-2 mb-1">
              الرسم لكل مرافق (د.ع)
            </label>
            <input
              type="number"
              className="input"
              min="0"
              step="250"
              value={companionFee}
              onChange={(e) => setCompanionFee(Math.max(0, Math.round(Number(e.target.value) || 0)))}
            />
          </div>
          <div className="flex gap-1.5">
            {[0, 1000, 2000, 5000].map((v) => (
              <button
                key={v}
                type="button"
                className={clsx('btn btn-sm', Number(companionFee) === v ? 'btn-primary' : '')}
                onClick={() => setCompanionFee(v)}
              >
                {v === 0 ? 'مجاناً' : v.toLocaleString('en-US')}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="btn btn-primary ms-auto"
            onClick={() => updateMut.mutate({ companionEntryFee: Number(companionFee) || 0 })}
            disabled={isLoading || updateMut.isPending || !feeDirty}
          >
            <Icon name="check" size={14} />
            {updateMut.isPending ? 'جاري الحفظ...' : 'حفظ الرسم'}
          </button>
        </div>
      </Card>

      <Card header="كلفة المنتج عند الشراء">
        <div className="text-ink-3 text-[12px] mb-3">
          تُطبَّق هذه السياسة فقط عند تفعيل خيار <b>«تحديث كلفة المنتج من هذه الفاتورة»</b> في
          فاتورة الشراء. إن كان الخيار مطفأً فكلفة المنتج لا تتغيّر إطلاقاً. لا تمسّ هذه
          السياسة أسعار الفواتير السابقة ولا تقييم حركات المخزون المسجّلة.
        </div>
        <div className="grid grid-cols-2 gap-2">
          {COST_POLICIES.map((p) => {
            const selected = costPolicy === p.key;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => setCostPolicy(p.key)}
                className={clsx(
                  'text-start flex items-start gap-2.5 p-2.5 rounded-[10px] border transition-colors',
                  selected ? 'border-orange bg-[#FFFCF5]' : 'border-line bg-white'
                )}
              >
                <span className="text-[15px] leading-none mt-0.5">{p.icon}</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[12.5px] font-semibold">{p.label}</span>
                  <span className="block text-[11px] text-ink-3 leading-relaxed">{p.sub}</span>
                </span>
                <span className={clsx('text-[11px] font-semibold', selected ? 'text-orange-deep' : 'text-ink-3')}>
                  {selected ? 'مُفعّلة' : ''}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex justify-end mt-4">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => updateMut.mutate({ productCostPolicy: costPolicy })}
            disabled={isLoading || updateMut.isPending || !costPolicyDirty}
          >
            <Icon name="check" size={14} />
            {updateMut.isPending ? 'جاري الحفظ...' : 'حفظ السياسة'}
          </button>
        </div>
      </Card>

      <Card header="فترات العمل (الشفتات)">
        <div className="space-y-2.5">
          {[
            {
              key: 'requireShift',
              value: data?.requireShift !== false,
              title: 'إلزام الشفت',
              hint: 'لا بيع ولا عملية نقدية (استقبال، محاسبة، نقاط بيع، تعبئة كروت) بدون شفت مفتوح. أطفئه للطوارئ فقط — العمليات بلا شفت لا تظهر في تسوية أي صندوق.'
            },
            {
              key: 'blindShiftCount',
              value: !!data?.blindShiftCount,
              title: 'العدّ الأعمى',
              hint: 'الكاشير لا يرى النقد المتوقع عند الإقفال، فيعدّ الصندوق دون رقم مسبق. المدير ومن يملك صلاحية «رؤية النقد المتوقع» يرون كل شيء.'
            }
          ].map((opt) => (
            <div key={opt.key} className="flex items-start gap-2.5 p-2.5 rounded-[10px] border border-line">
              <Toggle
                checked={opt.value}
                onChange={(v) => updateMut.mutate({ [opt.key]: v })}
              />
              <div className="flex-1">
                <div className="text-[12.5px] font-semibold">{opt.title}</div>
                <div className="text-[11.5px] text-ink-3 leading-relaxed">{opt.hint}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card header="تقريب إجمالي التسوية">
        <div className="text-ink-3 text-[12px] mb-3">
          يُقرّب المبلغ النهائي المستحق عند التسوية للأعلى لأقرب هذه القيمة (مثال: 5,200 → 6,000).
          اضبطها على <b>0</b> لإيقاف التقريب. لا يؤثر على أسعار الكافتيريا أو المنتجات المفردة.
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {ROUND_PRESETS.map((v) => (
            <button
              key={v}
              type="button"
              className={clsx(
                'btn',
                roundClean === v ? 'btn-primary' : ''
              )}
              onClick={() => setRoundUpTo(v)}
            >
              {v === 0 ? 'بدون تقريب' : v.toLocaleString('en-US')}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="0"
            step="250"
            className="input w-40"
            value={roundUpTo}
            onChange={(e) => setRoundUpTo(Number(e.target.value) || 0)}
          />
          <span className="text-ink-3 text-[12px]">د.ع</span>
        </div>
        <div className="flex justify-end mt-4">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => updateMut.mutate({ roundUpTo: roundClean })}
            disabled={isLoading || updateMut.isPending || !roundDirty}
          >
            <Icon name="check" size={14} />
            {updateMut.isPending ? 'جاري الحفظ...' : 'حفظ التغييرات'}
          </button>
        </div>
      </Card>
    </div>
  );
}
